import { useId, useMemo } from 'react'
import type { FlowEdge } from './markdown'

const FONT = 12.5
const LINE = 16
const CHAR = 6.9
const MAX_CHARS = 22
const PAD_X = 12
const PAD_Y = 9
const GAP_X = 48
const GAP_Y = 20
const LABEL_CHAR = 5.9
const LABEL_H = 17
const labelWidth = (label: string) => label.length * LABEL_CHAR + 10
const MARGIN = 6

function wrap(text: string): string[] {
  const words = text.split(/\s+/)
  const lines: string[] = []
  let current = ''
  for (const word of words) {
    if (current && (current + ' ' + word).length > MAX_CHARS) {
      lines.push(current)
      current = word
    } else current = current ? `${current} ${word}` : word
  }
  if (current) lines.push(current)
  return lines
}

type Node = { name: string; lines: string[]; width: number; height: number; depth: number; x: number; y: number }

/**
 * Lays a flow out left to right: a node's column is its longest path from a
 * start node, ignoring edges that close a cycle (those curve back underneath).
 */
function layout(edges: FlowEdge[]) {
  const names: string[] = []
  const add = (name: string) => !names.includes(name) && names.push(name)
  edges.forEach((edge) => (add(edge.from), add(edge.to)))

  const outgoing = new Map(names.map((name) => [name, edges.filter((edge) => edge.from === name)]))
  const back = new Set<FlowEdge>()
  const state = new Map<string, 'open' | 'done'>()
  const visit = (name: string) => {
    state.set(name, 'open')
    for (const edge of outgoing.get(name)!) {
      const next = state.get(edge.to)
      if (next === 'open') back.add(edge)
      else if (!next) visit(edge.to)
    }
    state.set(name, 'done')
  }
  names.forEach((name) => !state.has(name) && visit(name))

  const depth = new Map(names.map((name) => [name, 0]))
  for (let pass = 0; pass < names.length; pass++) {
    let changed = false
    for (const edge of edges) {
      if (back.has(edge)) continue
      const next = depth.get(edge.from)! + 1
      if (next > depth.get(edge.to)!) {
        depth.set(edge.to, next)
        changed = true
      }
    }
    if (!changed) break
  }

  const nodes = new Map<string, Node>()
  for (const name of names) {
    const lines = wrap(name)
    const width = Math.ceil(Math.max(...lines.map((line) => line.length)) * CHAR + PAD_X * 2)
    nodes.set(name, { name, lines, width, height: lines.length * LINE + PAD_Y * 2, depth: depth.get(name)!, x: 0, y: 0 })
  }

  const columns: Node[][] = []
  for (const node of nodes.values()) (columns[node.depth] ??= []).push(node)
  const columnWidth = columns.map((column) => Math.max(...column.map((node) => node.width)))
  const columnHeight = columns.map((column) => column.reduce((sum, node) => sum + node.height, 0) + GAP_Y * (column.length - 1))
  const height = Math.max(...columnHeight)

  // A forward edge's label sits in the gap right after its source column, so that gap is as wide as its widest label.
  const gaps = columns.map((_, index) =>
    Math.max(
      GAP_X,
      ...edges
        .filter((edge) => !back.has(edge) && edge.label && nodes.get(edge.from)!.depth === index && nodes.get(edge.to)!.depth > index)
        .map((edge) => labelWidth(edge.label!) + 28),
    ),
  )

  let x = MARGIN
  const columnX: number[] = []
  columns.forEach((column, index) => {
    columnX[index] = x
    let y = MARGIN + (height - columnHeight[index]) / 2
    for (const node of column) {
      node.x = x + (columnWidth[index] - node.width) / 2
      node.y = y
      y += node.height + GAP_Y
    }
    x += columnWidth[index] + gaps[index]
  })
  const width = x - gaps[columns.length - 1] + MARGIN
  const bottom = height + MARGIN

  const geometry: Geometry[] = edges.map((edge) => {
    const from = nodes.get(edge.from)!
    const to = nodes.get(edge.to)!
    if (back.has(edge) || to.depth <= from.depth) {
      const x1 = from.x + from.width / 2
      const x2 = to.x + to.width / 2
      const low = bottom + 22
      return { edge, path: `M${x1} ${from.y + from.height} C${x1} ${low}, ${x2} ${low}, ${x2} ${to.y + to.height + 2}`, lx: (x1 + x2) / 2, ly: low - 5 }
    }
    const x1 = from.x + from.width
    const y1 = from.y + from.height / 2
    const x2 = to.x - 2
    const y2 = to.y + to.height / 2
    const bend = Math.max(18, Math.min(80, (x2 - x1) / 2))
    // The label's point on the curve: the middle of the gap after the source column.
    const target = Math.min((x1 + x2) / 2, columnX[from.depth] + columnWidth[from.depth] + gaps[from.depth] / 2)
    let t = 0.5
    for (let step = 0; step < 24; step++) {
      const at = cubic(x1, x1 + bend, x2 - bend, x2, t)
      t += (target - at) / Math.max(1, x2 - x1)
      t = Math.min(1, Math.max(0, t))
    }
    return {
      edge,
      path: `M${x1} ${y1} C${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`,
      lx: cubic(x1, x1 + bend, x2 - bend, x2, t),
      ly: cubic(y1, y1, y2, y2, t),
    }
  })

  // Labels that would overlap are pushed apart vertically.
  const labelled = geometry.filter((item) => item.edge.label).sort((a, b) => a.ly - b.ly)
  for (let index = 1; index < labelled.length; index++) {
    const current = labelled[index]
    for (const other of labelled.slice(0, index)) {
      const overlapX = Math.abs(current.lx - other.lx) < (labelWidth(current.edge.label!) + labelWidth(other.edge.label!)) / 2 + 4
      if (overlapX && current.ly - other.ly < LABEL_H + 3) current.ly = other.ly + LABEL_H + 3
    }
  }
  const lowest = Math.max(bottom + (back.size ? 34 : 0), ...labelled.map((item) => item.ly + LABEL_H / 2 + 2))

  return { nodes, geometry, width, height: lowest + MARGIN }
}

type Geometry = { edge: FlowEdge; path: string; lx: number; ly: number }

const cubic = (a: number, b: number, c: number, d: number, t: number) =>
  (1 - t) ** 3 * a + 3 * (1 - t) ** 2 * t * b + 3 * (1 - t) * t ** 2 * c + t ** 3 * d

export function FlowDiagram({ edges, label }: { edges: FlowEdge[]; label?: string }) {
  const id = useId().replace(/:/g, '')
  const { nodes, geometry, width, height } = useMemo(() => layout(edges), [edges])
  const starts = new Set([...nodes.keys()].filter((name) => !edges.some((edge) => edge.to === name && nodes.get(edge.from)!.depth < nodes.get(name)!.depth)))
  const ends = new Set([...nodes.keys()].filter((name) => !edges.some((edge) => edge.from === name)))

  return (
    <figure className="my-5 overflow-x-auto rounded-[calc(14px*var(--radius-scale))] border border-line bg-[var(--glass-solid)] px-4 py-4">
      <svg
        role="img"
        aria-label={label ?? edges.map((edge) => `${edge.from} → ${edge.to}`).join(', ')}
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        style={{ maxWidth: '100%', minWidth: Math.min(width, Math.max(520, width * 0.85)) }}
        className="mx-auto block h-auto"
      >
        <defs>
          <marker id={`${id}-arrow`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0 1 L9 5 L0 9 z" fill="var(--subtle)" />
          </marker>
        </defs>

        {geometry.map(({ path }, index) => (
          <path key={index} d={path} fill="none" stroke="var(--line-strong)" strokeWidth={1.4} markerEnd={`url(#${id}-arrow)`} />
        ))}
        {geometry.map(({ edge, lx, ly }, index) =>
          edge.label ? (
            <g key={index} transform={`translate(${lx} ${ly})`}>
              <rect x={-labelWidth(edge.label) / 2} y={-LABEL_H / 2} width={labelWidth(edge.label)} height={LABEL_H} rx={LABEL_H / 2} fill="var(--glass-solid)" stroke="var(--line)" />
              <text textAnchor="middle" dominantBaseline="central" fontSize={10.5} fill="var(--muted)">
                {edge.label}
              </text>
            </g>
          ) : null,
        )}

        {[...nodes.values()].map((node) => {
          const start = starts.has(node.name)
          const end = ends.has(node.name)
          return (
            <g key={node.name} transform={`translate(${node.x} ${node.y})`}>
              <rect
                width={node.width}
                height={node.height}
                rx={Math.min(12, node.height / 2)}
                fill={start ? 'var(--accent-soft)' : 'var(--canvas)'}
                stroke={start ? 'var(--accent)' : end ? 'var(--line-strong)' : 'var(--line)'}
                strokeWidth={start ? 1.2 : 1}
              />
              {node.lines.map((line, index) => (
                <text
                  key={index}
                  x={node.width / 2}
                  y={PAD_Y + LINE * index + LINE / 2}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={FONT}
                  fontWeight={start || end ? 600 : 500}
                  fill={start ? 'var(--accent)' : 'var(--fg)'}
                >
                  {line}
                </text>
              ))}
            </g>
          )
        })}
      </svg>
    </figure>
  )
}
